/**
 * CabinetViewer — the top-level component for the "3D View" tab.
 * Loads area data, line items, and construction methods, then renders CabinetScene.
 * Includes area selector dropdown + toolbar.
 */

import { useState, useMemo, Suspense } from 'react';
import { Select, SelectItem, Spinner, Button, Chip, Checkbox } from '@heroui/react';
import { RotateCcw, Maximize2, Box, Wrench, CircleDot } from 'lucide-react';
import { useAreas } from '@/api/areas';
import { useLineItems } from '@/api/line-items';
import { useConstructionMethods } from '@/api/construction-methods';
import { lineItemToCabinetData } from './dataAdapter';
import type { CabinetData } from './types';
import CabinetScene from './CabinetScene';

interface CabinetViewerProps {
  projectId: string;
}

export default function CabinetViewer({ projectId }: CabinetViewerProps) {
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null);
  const [selectedCabinetId, setSelectedCabinetId] = useState<string | null>(null);
  const [sceneKey, setSceneKey] = useState(0); // for reset
  const [showHardware, setShowHardware] = useState(true);
  const [showDrillHoles, setShowDrillHoles] = useState(true);

  // ── Data loading ──────────────────────────────────────────────────────────
  const { data: areas, isLoading: areasLoading } = useAreas(projectId);
  const { data: constructionMethodsList } = useConstructionMethods();

  // Auto-select first area
  const activeAreaId = selectedAreaId ?? areas?.[0]?.id ?? null;
  const { data: lineItems, isLoading: itemsLoading } = useLineItems(activeAreaId ?? undefined);

  // Build construction methods lookup map
  const constructionMethods = useMemo(() => {
    const map = new Map<string, Record<string, unknown>>();
    if (constructionMethodsList) {
      for (const cm of constructionMethodsList) {
        map.set((cm as Record<string, unknown>).id as string, cm as Record<string, unknown>);
      }
    }
    return map;
  }, [constructionMethodsList]);

  // Convert line items to CabinetData
  const cabinets = useMemo(() => {
    if (!lineItems) return [];
    const cabs: CabinetData[] = [];
    let autoX = 0;
    const GAP = 6; // 6" gap for auto-positioned cabinets

    for (const item of lineItems) {
      const raw = item as unknown as Record<string, unknown>;
      const cab = lineItemToCabinetData(raw, constructionMethods);
      if (!cab) continue;

      // Auto-position cabinets that haven't been placed
      if (cab.position_x === 0 && cab.position_z === 0) {
        cab.position_x = autoX;
        autoX += cab.width + GAP;
      }

      // Handle quantity > 1: create instances side by side
      for (let q = 0; q < cab.quantity; q++) {
        if (q === 0) {
          cabs.push(cab);
        } else {
          cabs.push({
            ...cab,
            id: `${cab.id}_inst${q}`,
            position_x: cab.position_x + (cab.width + GAP) * q,
          });
          autoX += cab.width + GAP;
        }
      }
    }

    return cabs;
  }, [lineItems, constructionMethods]);

  // ── Selected cabinet info ─────────────────────────────────────────────────
  const selectedCab = cabinets.find((c) => c.id === selectedCabinetId);

  const isLoading = areasLoading || itemsLoading;

  return (
    <div className="flex flex-col h-full" style={{ minHeight: 'calc(100vh - 180px)' }}>
      {/* ── Toolbar ────────────────────────────────────────────────────────── */}
      <div
        className="flex items-center gap-3 px-4 py-2 border-b shrink-0"
        style={{
          borderColor: 'var(--border)',
          backgroundColor: 'var(--bg-surface)',
        }}
      >
        <Box size={14} style={{ color: 'var(--text-muted)' }} />
        <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>
          3D View
        </span>

        {/* Area selector */}
        <Select
          size="sm"
          aria-label="Select area"
          className="w-48"
          selectedKeys={activeAreaId ? [activeAreaId] : []}
          onSelectionChange={(keys) => {
            const k = Array.from(keys)[0] as string;
            setSelectedAreaId(k || null);
            setSelectedCabinetId(null);
          }}
          classNames={{
            trigger: 'h-7 min-h-7 bg-[var(--bg-elevated)] border-[var(--border)]',
            value: 'text-xs text-[var(--text-primary)]',
          }}
        >
          {(areas ?? []).map((area: Record<string, unknown>) => (
            <SelectItem key={area.id as string} textValue={area.name as string}>
              <span className="text-xs">{area.name as string}</span>
            </SelectItem>
          ))}
        </Select>

        {/* Cabinet count */}
        {!isLoading && (
          <Chip size="sm" variant="flat" className="text-[10px]">
            {cabinets.length} cabinet{cabinets.length !== 1 ? 's' : ''}
          </Chip>
        )}

        {/* Hardware toggles */}
        <div className="flex items-center gap-3 ml-2">
          <label className="flex items-center gap-1.5 cursor-pointer">
            <Checkbox
              size="sm"
              isSelected={showHardware}
              onValueChange={setShowHardware}
              classNames={{ wrapper: 'mr-0' }}
            />
            <Wrench size={12} style={{ color: 'var(--text-muted)' }} />
            <span className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>
              Hardware
            </span>
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <Checkbox
              size="sm"
              isSelected={showDrillHoles}
              onValueChange={setShowDrillHoles}
              classNames={{ wrapper: 'mr-0' }}
            />
            <CircleDot size={12} style={{ color: 'var(--text-muted)' }} />
            <span className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>
              Drill Holes
            </span>
          </label>
        </div>

        <div className="flex-1" />

        {/* Selected cabinet info */}
        {selectedCab && (
          <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
            {selectedCab.code || selectedCab.description}
            <span style={{ color: 'var(--text-muted)', marginLeft: 6 }}>
              {selectedCab.width}"×{selectedCab.height}"×{selectedCab.depth}"
            </span>
          </span>
        )}

        {/* Reset view */}
        <Button
          isIconOnly
          size="sm"
          variant="light"
          onPress={() => setSceneKey((k) => k + 1)}
          className="text-[var(--text-muted)] h-7 w-7 min-w-7"
        >
          <RotateCcw size={13} />
        </Button>
      </div>

      {/* ── 3D Canvas ──────────────────────────────────────────────────────── */}
      <div className="flex-1 relative">
        {isLoading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <Spinner size="lg" color="primary" />
          </div>
        ) : cabinets.length === 0 ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
            <Box size={32} style={{ color: 'var(--text-muted)' }} />
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              No cabinets with dimensions in this area
            </p>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Add line items with width, height, and depth to see them here
            </p>
          </div>
        ) : (
          <div className="absolute inset-0">
            <Suspense
              fallback={
                <div className="flex items-center justify-center h-full">
                  <Spinner size="lg" color="primary" />
                </div>
              }
            >
              <CabinetScene
                key={sceneKey}
                cabinets={cabinets}
                selectedId={selectedCabinetId}
                onSelect={setSelectedCabinetId}
                showHardware={showHardware}
                showDrillHoles={showDrillHoles}
                style={{ width: '100%', height: '100%' }}
              />
            </Suspense>
          </div>
        )}
      </div>
    </div>
  );
}
